import { useState } from "react";
import { ArrowUpRight, CheckCircle2, Globe, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { CONTACT, SERVICES } from "@/data/site";

const WORKFORCE_OPTIONS = [...SERVICES.map((s) => s.title), "Other / Multiple Requirements"];
const DURATION_OPTIONS = ["Less than 1 month", "1–3 months", "3–6 months", "6–12 months", "12+ months", "Ongoing"];

const inputCls =
  "w-full border border-navy/15 bg-white px-4 py-3 text-sm text-charcoal outline-none transition-colors placeholder:text-charcoal/35 focus:border-gold focus:ring-1 focus:ring-gold";

const EMPTY = {
  name: "", company: "", position: "", phone: "", email: "", project_location: "",
  workforce_required: "", personnel_count: "", start_date: "", duration: "", message: "", consent: false,
};

function Field({ label, htmlFor, required, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/70">
        {label} {required && <span className="text-gold">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("Please confirm permission for Formcore to contact you.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/enquiries", form);
      setResult(data);
      toast.success("Enquiry submitted — our team will be in touch.");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        index="06"
        label="Contact"
        titleLines={["Let's Talk About Your", { t: "Workforce Requirements.", c: "text-gold" }]}
        intro="Whether you are planning a new project, scaling an existing workforce or need additional personnel at short notice, Formcore Constructions is ready to discuss your requirements."
      />

      <section className="bg-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <div className="h-full bg-navy p-9 text-white" data-testid="contact-details-card">
                <h2 className="font-heading text-xl font-extrabold tracking-tight">Formcore Constructions</h2>
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                  Construction Workforce. Built for Performance.
                </p>
                <div className="mt-9 space-y-6 text-sm">
                  <div className="flex items-start gap-4">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">Head Office</p>
                      <p className="mt-1 text-white/85">{CONTACT.address1}<br />{CONTACT.address2}</p>
                    </div>
                  </div>
                  <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="flex items-start gap-4 transition-colors hover:text-gold">
                    <Phone className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">Phone</p>
                      <p className="mt-1">{CONTACT.phone}</p>
                    </div>
                  </a>
                  <a href={CONTACT.emailHref} data-testid="contact-email-link" className="flex items-start gap-4 transition-colors hover:text-gold">
                    <Mail className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">Email</p>
                      <p className="mt-1 break-all">{CONTACT.email}</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-4">
                    <Globe className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">Website</p>
                      <p className="mt-1 text-white/85">{CONTACT.website}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-10 border-t border-white/10 pt-6">
                  <p className="text-[13px] leading-relaxed text-white/55">
                    Our management team responds to workforce enquiries promptly. For urgent personnel requirements,
                    call us directly.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12} className="lg:col-span-8">
              <div id="enquiry-form" className="border border-navy/10 bg-white p-8 md:p-10">
                {result ? (
                  <div className="flex flex-col items-start gap-5 py-10" data-testid="enquiry-success">
                    <CheckCircle2 className="h-12 w-12 text-gold" />
                    <h2 className="font-heading text-3xl font-extrabold tracking-tight text-navy">Enquiry Received</h2>
                    <p className="max-w-md text-sm leading-relaxed text-charcoal/70">
                      Thank you, {result && form.name.split(" ")[0]}. Your workforce enquiry has been received and a
                      member of our management team will be in touch to discuss your requirements.
                    </p>
                    <p className="text-xs uppercase tracking-[0.2em] text-charcoal/45">
                      Reference <span className="font-bold text-navy">{result.id.slice(0, 8).toUpperCase()}</span>
                    </p>
                    <button
                      onClick={() => { setResult(null); setForm(EMPTY); }}
                      data-testid="enquiry-another-btn"
                      className="mt-2 border-2 border-navy px-6 py-3 font-heading text-xs font-bold uppercase tracking-[0.15em] text-navy transition-colors hover:bg-navy hover:text-white"
                    >
                      Submit Another Enquiry
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="enquiry-form">
                    <h2 className="font-heading text-2xl font-extrabold tracking-tight text-navy">Request Workforce</h2>
                    <p className="mt-2 text-sm text-charcoal/60">
                      Tell us about your project and workforce requirements. Fields marked * are required.
                    </p>

                    <div className="mt-8 grid gap-5 sm:grid-cols-2">
                      <Field label="Name" htmlFor="f-name" required>
                        <input id="f-name" data-testid="enquiry-name-input" className={inputCls} value={form.name} onChange={set("name")} required placeholder="Your full name" />
                      </Field>
                      <Field label="Company" htmlFor="f-company" required>
                        <input id="f-company" data-testid="enquiry-company-input" className={inputCls} value={form.company} onChange={set("company")} required placeholder="Company name" />
                      </Field>
                      <Field label="Position" htmlFor="f-position">
                        <input id="f-position" data-testid="enquiry-position-input" className={inputCls} value={form.position} onChange={set("position")} placeholder="e.g. Project Manager" />
                      </Field>
                      <Field label="Phone" htmlFor="f-phone" required>
                        <input id="f-phone" data-testid="enquiry-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} required placeholder="Your contact number" />
                      </Field>
                      <Field label="Email" htmlFor="f-email" required>
                        <input id="f-email" data-testid="enquiry-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} required placeholder="you@company.com.au" />
                      </Field>
                      <Field label="Project Location" htmlFor="f-location" required>
                        <input id="f-location" data-testid="enquiry-location-input" className={inputCls} value={form.project_location} onChange={set("project_location")} required placeholder="e.g. Sydney NSW" />
                      </Field>
                      <Field label="Workforce Required" htmlFor="f-workforce" required>
                        <select id="f-workforce" data-testid="enquiry-workforce-select" className={inputCls} value={form.workforce_required} onChange={set("workforce_required")} required>
                          <option value="">Select workforce type</option>
                          {WORKFORCE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </Field>
                      <Field label="Number of Personnel Required" htmlFor="f-personnel">
                        <input id="f-personnel" data-testid="enquiry-personnel-input" className={inputCls} value={form.personnel_count} onChange={set("personnel_count")} placeholder="e.g. 5" />
                      </Field>
                      <Field label="Expected Start Date" htmlFor="f-start">
                        <input id="f-start" data-testid="enquiry-start-input" type="date" className={inputCls} value={form.start_date} onChange={set("start_date")} />
                      </Field>
                      <Field label="Duration" htmlFor="f-duration">
                        <select id="f-duration" data-testid="enquiry-duration-select" className={inputCls} value={form.duration} onChange={set("duration")}>
                          <option value="">Select duration</option>
                          {DURATION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="Message / Requirements" htmlFor="f-message">
                          <textarea id="f-message" data-testid="enquiry-message-input" rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your project, site requirements, skills needed..." />
                        </Field>
                      </div>
                    </div>

                    <label className="mt-7 flex cursor-pointer items-start gap-3" htmlFor="f-consent">
                      <input
                        id="f-consent"
                        type="checkbox"
                        data-testid="enquiry-consent-checkbox"
                        checked={form.consent}
                        onChange={set("consent")}
                        className="mt-1 h-4 w-4 shrink-0 accent-[#D89B24]"
                      />
                      <span className="text-[13px] leading-relaxed text-charcoal/70">
                        I give Formcore Constructions permission to contact me regarding this workforce enquiry. *
                      </span>
                    </label>

                    {error && (
                      <p className="mt-5 border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="enquiry-error">
                        {error}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="enquiry-submit-btn"
                      className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-gold px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.12em] text-navy transition-colors duration-200 hover:bg-gold-dark active:scale-[0.98] disabled:opacity-60 sm:w-auto"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                      {loading ? "Submitting..." : "Submit Workforce Enquiry"}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
